'use client'

import { createContext, useEffect, useState, useRef, useContext } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard,
  Users,
  Shield,
  Smartphone,
  Settings,
  ClipboardList,
  DollarSign,
  Receipt,
  ChevronLeft,
  ChevronDown,
  Layers,
  Link2,
  CheckSquare,
  BarChart3,
  GitBranch,
  FileText,
  CreditCard,
  UserPlus,
  Image,
  UserCheck,
  TrendingUp,
  Megaphone,
  Calendar,
  ClipboardCheck,
  X,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { getUserMenus, type MenuTreeNode } from '@/pages/platform/system/role/role-api'
import { Spinner } from '@/components/ui/spinner'
import { useMediaQuery } from '@/hooks/useMediaQuery'

/** 桌面/移动布局分界断点（与 Tailwind md: 一致） */
const DESKTOP_QUERY = '(min-width: 768px)'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
  /** 移动端抽屉是否打开（md 以下生效） */
  mobileOpen: boolean
  /** 关闭移动端抽屉（遮罩点击 / 顶部关闭按钮 / 菜单跳转后） */
  onMobileClose: () => void
}

interface ActiveMenuContextType {
  activeMenuId: string | null
  setActiveMenuId: (id: string | null) => void
  /** 手风琴模式：当前展开的菜单ID */
  expandedId: string | null
  /** 设置展开的菜单ID（null 表示全部闭合） */
  setExpandedId: (id: string | null) => void
  /** 移动端：叶子菜单跳转后收起抽屉（桌面为 no-op） */
  closeMobileNav: () => void
}

const ActiveMenuContext = createContext<ActiveMenuContextType | null>(null)

const iconMap: Record<string, LucideIcon> = {
  LayoutDashboard,
  Users,
  Shield,
  Settings,
  ClipboardList,
  DollarSign,
  Receipt,
  Layers,
  Link2,
  CheckSquare,
  BarChart3,
  GitBranch,
  FileText,
  CreditCard,
  UserPlus,
  Image,
  UserCheck,
  TrendingUp,
  Smartphone,
}

const codeIconMap: Record<string, LucideIcon> = {
  welcome: LayoutDashboard,
  system: Settings,
  'system:user': Users,
  'system:role': Shield,
  'system:menu': Layers,
  'system:banner': Image,
  'system:activity': Megaphone,
  'system:operation-log': FileText,
  'system:app-version': Smartphone,
  customer: UserPlus,
  'customer:list': Users,
  'customer:bank-card': CreditCard,
  'customer:referral': UserPlus,
  'customer:affiliate-user': Users,
  'customer:team-members': Users,
  'customer:active-log': Calendar,
  'customer:kyc': UserCheck,
  'customer:user-apply': ClipboardCheck,
  product: ClipboardList,
  'product:manage': ClipboardList,
  'product:category': Layers,
  affiliate: TrendingUp,
  order: TrendingUp,
  'order:relation': Link2,
  'order:site': Layers,
  'order:position': Layers,
  'order:material': Link2,
  'order:trace': GitBranch,
  'order:conversion': FileText,
  'order:commission': DollarSign,
  'order:stats': BarChart3,
  finance: DollarSign,
  'finance:overview': BarChart3,
  'finance:account': DollarSign,
  'finance:detail': Receipt,
  transaction: CreditCard,
  'transaction:list': Receipt,
  'transaction:record': Receipt,
  'transaction:review': CheckSquare,
}

function MenuIcon({ iconName, menuCode, className }: { iconName?: string; menuCode?: string; className?: string }) {
  const Icon = iconName && iconMap[iconName] ? iconMap[iconName]
    : menuCode && codeIconMap[menuCode] ? codeIconMap[menuCode]
    : menuCode && codeIconMap[menuCode.split(':')[0]] ? codeIconMap[menuCode.split(':')[0]]
    : LayoutDashboard
  return <Icon className={className} />
}

interface SubMenuPanelProps {
  node: MenuTreeNode
  position: { top: number }
  onMouseEnter: () => void
  onMouseLeave: () => void
}

function SubMenuPanel({ node, position, onMouseEnter, onMouseLeave }: SubMenuPanelProps) {
  const renderSubItems = (items: MenuTreeNode[]): React.ReactNode => {
    return items.map((child, index) => {
      if (child.menuType === 3) return null

      if (child.children && child.children.length > 0) {
        return (
          <div key={child.id || `sub-${index}`} className="space-y-0.5">
            <div className="px-3 py-1.5 text-xs text-neutral-500 font-medium">
              {child.menuName}
            </div>
            {renderSubItems(child.children)}
          </div>
        )
      }

      if (!child.path) return null

      return (
        <NavLink
          key={child.id || `sub-${index}`}
          to={child.path}
          className={({ isActive: navIsActive }) =>
            cn(
              'flex items-center gap-2 px-3 py-1.5 rounded text-sm',
              navIsActive
                ? 'bg-primary-500/10 text-primary-400'
                : 'text-neutral-300 hover:bg-neutral-700/50 hover:text-white'
            )
          }
        >
          <MenuIcon iconName={child.icon} menuCode={child.menuCode} className="h-4 w-4 flex-shrink-0" />
          <span>{child.menuName}</span>
        </NavLink>
      )
    })
  }

  return (
    <div
      className="fixed z-[100] bg-neutral-800 border border-neutral-600 rounded-lg shadow-xl py-2 min-w-[180px]"
      style={{
        left: 64 + 8,
        top: position.top,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="px-3 py-2 border-b border-neutral-700">
        <span className="text-sm font-medium text-white">{node.menuName}</span>
      </div>
      <div className="py-1">
        {renderSubItems(node.children || [])}
      </div>
    </div>
  )
}

interface MenuItemProps {
  node: MenuTreeNode
  collapsed: boolean
  level?: number
  /** 父菜单ID（用于叶子菜单点击时保持父菜单展开） */
  parentMenuId?: string
}

function MenuItem({ node, collapsed, level = 0, parentMenuId }: MenuItemProps) {
  const location = useLocation()
  const itemRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // 标记是否已完成初始化（用于控制自动展开行为只执行一次）
  const initializedRef = useRef(false)
  const context = useContext(ActiveMenuContext)
  const hasChildren = node.children && node.children.length > 0

  // 手风琴模式：从 context 获取展开状态
  const expanded = context?.expandedId === node.id

  const isActive = (path: string) => {
    if (path === '/welcome') {
      // 欢迎页路由为 /{bizLine}/welcome，匹配任意业务线
      return /\/welcome$/.test(location.pathname)
    }
    return location.pathname.startsWith(path)
  }

  // 检查当前节点或其子节点是否被激活
  const isNodeActive = hasChildren 
    ? node.children!.some(child => isActive(child.path || ''))
    : isActive(node.path || '')

  // 只在首次加载且菜单树可用时，自动展开包含当前路由的菜单
  // 之后用户可以自由切换，不再自动改变
  useEffect(() => {
    if (initializedRef.current) return
    if (hasChildren && isNodeActive) {
      context?.setExpandedId(node.id)
      initializedRef.current = true
    }
  }, [hasChildren, isNodeActive, node.id, context])

  const showSubMenu = context?.activeMenuId === node.id && hasChildren && collapsed

  useEffect(() => {
    if (context?.activeMenuId !== node.id && closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [context?.activeMenuId, node.id])

  const handleMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    context?.setActiveMenuId(node.id)
  }

  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      context?.setActiveMenuId(null)
    }, 150)
  }

  /**
   * 切换菜单展开状态（手风琴模式）
   * - 点击已展开的菜单 → 闭合
   * - 点击其他菜单 → 展开该菜单，闭合其他
   */
  const handleToggle = () => {
    if (expanded) {
      context?.setExpandedId(null)
    } else {
      context?.setExpandedId(node.id)
    }
  }

  if (node.menuType === 3) return null

  const basePadding = 12
  const levelPadding = level * 8

  if (hasChildren && collapsed) {
    return (
      <div
        ref={itemRef}
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          className={cn(
            'flex items-center justify-center gap-2.5 py-2 rounded-md text-sm font-medium w-full',
            isActive(node.path || '') || node.children!.some(child => isActive(child.path || ''))
              ? 'bg-neutral-700/50 text-white'
              : 'text-neutral-400 hover:bg-neutral-700/30 hover:text-white',
            'px-3'
          )}
        >
          <MenuIcon iconName={node.icon} menuCode={node.menuCode} className="h-4 w-4 flex-shrink-0" />
        </button>

        {/* eslint-disable react-hooks/refs */}
        {showSubMenu && itemRef.current && (
          <SubMenuPanel
            node={node}
            position={{ top: itemRef.current.getBoundingClientRect().top }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        )}
        {/* eslint-enable react-hooks/refs */}
      </div>
    )
  }

  if (hasChildren) {
    return (
      <div className="space-y-0.5">
        <button
          onClick={handleToggle}
          className={cn(
            'flex items-center gap-2.5 py-2 rounded-md text-sm font-medium w-full text-left',
            // 非展开状态：根据 active 状态设置样式
            !expanded && (
              isActive(node.path || '')
                ? 'bg-neutral-700/50 text-white'
                : 'text-neutral-200 hover:bg-neutral-700/30 hover:text-white'
            ),
            'px-3',
            // 展开状态：用内联样式设置主题色背景，避免 Tailwind v4 opacity 语法问题
            expanded && 'border-l-2'
          )}
          style={{
            paddingLeft: expanded ? basePadding + levelPadding - 2 : basePadding + levelPadding,
            // 展开时用主题色背景和边框，子菜单区域用深色区分
            backgroundColor: expanded ? 'rgba(31, 111, 235, 0.15)' : undefined,
            color: expanded ? '#ffffff' : undefined,
            borderColor: expanded ? '#1f6feb' : undefined,
          }}
        >
          <MenuIcon iconName={node.icon} menuCode={node.menuCode} className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1 truncate">{node.menuName}</span>
          <ChevronDown className={cn(
            'h-4 w-4 text-neutral-500 transition-transform',
            expanded && 'rotate-180'
          )} />
        </button>

        {expanded && (
          <div
            className="ml-2 pl-3 py-1 space-y-0.5"
            style={{ 
              borderLeft: '2px solid rgba(31, 111, 235, 0.5)',
              backgroundColor: '#2d333b'
            }}
          >
            {node.children!.map((child, index) => (
              <MenuItem
                key={child.id || `child-${index}`}
                node={child}
                collapsed={collapsed}
                level={level + 1}
                parentMenuId={node.id}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  if (!node.path) return null

  if (collapsed) {
    return (
      <NavLink
        to={node.path}
        onClick={() => {
          context?.setExpandedId(parentMenuId ?? null)
          context?.closeMobileNav()
        }}
        className={({ isActive: navIsActive }) =>
          cn(
            'flex items-center justify-center py-2 rounded-md text-sm font-medium',
            navIsActive
              ? 'bg-primary-500/10 text-primary-400'
              : 'text-neutral-400 hover:bg-neutral-700/30 hover:text-white',
            'px-3'
          )
        }
        title={node.menuName}
      >
        <MenuIcon iconName={node.icon} menuCode={node.menuCode} className="h-4 w-4 flex-shrink-0" />
      </NavLink>
    )
  }

  return (
    <NavLink
      to={node.path}
      onClick={() => {
        context?.setExpandedId(parentMenuId ?? null)
        context?.closeMobileNav()
      }}
      className={({ isActive: navIsActive }) =>
        cn(
          'flex items-center gap-2.5 py-2 rounded-md text-sm font-medium',
          navIsActive
            ? 'bg-primary-500/30 text-primary-200'
            : level > 0
              ? 'text-white hover:bg-neutral-500/40 hover:text-white'
              : 'text-neutral-200 hover:bg-neutral-700/30 hover:text-white',
          'px-3'
        )
      }
      style={{ paddingLeft: basePadding + levelPadding }}
    >
      <MenuIcon iconName={node.icon} menuCode={node.menuCode} className="h-4 w-4 flex-shrink-0" />
      <span className="truncate">{node.menuName}</span>
    </NavLink>
  )
}

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  // 当前 hover 激活的菜单（用于折叠状态下的弹出子菜单）
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null)
  // 手风琴模式：当前展开的菜单ID
  const [expandedId, setExpandedId] = useState<string | null>(null)
  // 移动端抽屉态始终渲染展开版菜单：折叠态的子菜单靠 hover 弹出，触屏没有 hover 无法使用
  const isDesktop = useMediaQuery(DESKTOP_QUERY)
  const effectiveCollapsed = isDesktop ? collapsed : false

  const { data: menuTree = [], isLoading } = useQuery({
    queryKey: ['userMenus'],
    queryFn: getUserMenus,
  })

  return (
    <ActiveMenuContext.Provider value={{
      activeMenuId,
      setActiveMenuId,
      expandedId,
      setExpandedId,
      closeMobileNav: () => {
        if (!isDesktop) onMobileClose()
      },
    }}>
      {/* 移动端遮罩：点击任意处关闭抽屉 */}
      {!isDesktop && mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={onMobileClose}
        />
      )}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-screen bg-neutral-900 border-r border-neutral-700 transition-all',
          effectiveCollapsed ? 'w-16' : 'w-56',
          // 移动端默认收在屏幕左侧外，打开抽屉时滑入；桌面端始终可见
          isDesktop || mobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="h-14 flex items-center px-3 border-b border-neutral-700">
          {!effectiveCollapsed ? (
            <>
              <div className="flex items-center gap-2 flex-1">
                <div className="flex items-center justify-center w-8 h-8 bg-primary-500 rounded-md">
                  <LayoutDashboard className="h-4 w-4 text-white" />
                </div>
                <span className="text-sm font-semibold text-white">
                  管理系统
                </span>
              </div>
              <button
                onClick={isDesktop ? onToggle : onMobileClose}
                className="p-1.5 rounded-md hover:bg-neutral-700 text-neutral-400 hover:text-white"
                title={isDesktop ? '收起菜单' : '关闭菜单'}
              >
                {isDesktop ? <ChevronLeft className="h-4 w-4" /> : <X className="h-4 w-4" />}
              </button>
            </>
          ) : (
            <div className="w-full flex justify-center">
              <button
                onClick={onToggle}
                className="flex items-center justify-center w-8 h-8 bg-primary-500 rounded-md hover:bg-primary-600"
                title="展开菜单"
              >
                <LayoutDashboard className="h-4 w-4 text-white" />
              </button>
            </div>
          )}
        </div>

        <nav className="p-2 space-y-0.5 overflow-y-auto h-[calc(100vh-3.5rem)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner className="h-5 w-5" />
            </div>
          ) : (
            menuTree.map((node, index) => (
              <MenuItem
                key={node.id || `menu-${index}`}
                node={node}
                collapsed={effectiveCollapsed}
              />
            ))
          )}
        </nav>
      </aside>
    </ActiveMenuContext.Provider>
  )
}